import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { dmrApi, type DmrPastCall, type DmrResponse, type DmrTemplateItem } from '../api/dmr';
import { showToast } from './Toast';

/**
 * The DMR run sheet as a plain checklist in the Notes drawer (#252). The call
 * happens inside the franchisee's dashboard on a shared screen, so the
 * checklist sits beside it: tick as you go, answers save themselves. Summary,
 * action items and the follow-up email stay on the call page.
 */

/** This AF's DMR calls across all years (#254): `current` is this season's
 *  call (editable), `past` the archive, newest first. */
export function useDmrHistory(af: string, enabled: boolean) {
  const q = useQuery({
    queryKey: ['dmr', 'history', af],
    queryFn: () => dmrApi.history(af),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
  const calls = q.data?.calls ?? [];
  return { current: calls.find((c) => c.current) ?? null, past: calls.filter((c) => !c.current) };
}

function fmtWhen(iso: string | null) {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function answered(item: DmrTemplateItem, r: DmrResponse | undefined) {
  if (!r) return false;
  return item.type === 'check' ? r.status === 'yes' : r.value !== undefined && r.value !== null && r.value !== '';
}

export default function DmrChecklistPane({ bookingId, year }: { bookingId: number; year: number | null }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['dmr', 'call', bookingId], queryFn: () => dmrApi.call(bookingId) });
  const [responses, setResponses] = useState<Record<string, DmrResponse>>({});
  const [saved, setSaved] = useState<'idle' | 'saving' | 'saved'>('idle');
  const loadedFor = useRef<number | null>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (q.data && loadedFor.current !== bookingId) {
      loadedFor.current = bookingId;
      setResponses(q.data.run?.responses ?? {});
    }
  }, [q.data, bookingId]);

  const save = useMutation({
    mutationFn: (next: Record<string, DmrResponse>) =>
      dmrApi.saveRun(bookingId, { responses: next }),
    onSuccess: () => setSaved('saved'),
    onError: (e) => {
      setSaved('idle');
      showToast(e instanceof Error ? e.message : 'Couldn’t save the checklist', { variant: 'error' });
    },
  });

  // Save shortly after the last change, so ticking several boxes is one request.
  function update(id: string, r: DmrResponse | null) {
    const next = { ...responses };
    if (r) next[id] = { ...next[id], ...r };
    else delete next[id];
    setResponses(next);
    setSaved('saving');
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => save.mutate(next), 600);
  }
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const complete = useMutation({
    mutationFn: async () => {
      window.clearTimeout(timer.current);
      await save.mutateAsync(responses);
      return dmrApi.completeCall(bookingId);
    },
    onSuccess: () => {
      showToast('Call marked complete. A summary note was added to their record.');
      qc.invalidateQueries({ queryKey: ['dmr'] });
      qc.invalidateQueries({ queryKey: ['franchisee-notes'] });
    },
    onError: (e) => showToast(e instanceof Error ? e.message : 'Couldn’t complete the call', { variant: 'error' }),
  });

  if (q.isLoading) return <div className="notes-empty">Loading the checklist…</div>;
  if (q.isError || !q.data) return <div className="notes-error">Couldn’t load the checklist.</div>;
  const { booking, template } = q.data;
  if (!template) return <div className="notes-empty">This season has no checklist yet.</div>;

  const items = template.sections.flatMap((s) => s.items);
  const done = items.filter((i) => answered(i, responses[i.id])).length;
  const canEdit = q.data.canEdit;

  return (
    <div className="dmr-check">
      <div className="dmr-check-head">
        <div>
          <div className="dmr-check-title">{year ? `${year} ` : ''}Digital Marketing Review</div>
          <div className="dmr-check-meta">
            {fmtWhen(booking.startsAt)}
            {booking.teamMember && <> · with {booking.teamMember.name}</>}
            {booking.status === 'completed' && <> · <strong>Complete</strong></>}
          </div>
        </div>
        <div className="dmr-check-progress" title={`${done} of ${items.length} done`}>
          {done}/{items.length}
        </div>
      </div>
      <div className="dmr-check-bar">
        <div style={{ width: `${items.length ? (done / items.length) * 100 : 0}%` }} />
      </div>

      {template.sections.map((section) => (
        <section key={section.id} className="dmr-check-section">
          <h4>{section.title}</h4>
          {section.items.map((item) => (
            <ChecklistItem
              key={item.id}
              item={item}
              value={responses[item.id]}
              disabled={!canEdit}
              onChange={(r) => update(item.id, r)}
            />
          ))}
        </section>
      ))}

      <div className="dmr-check-foot">
        <span className="dmr-check-saved">
          {saved === 'saving' ? 'Saving…' : saved === 'saved' ? 'All changes saved' : ''}
        </span>
        <span style={{ flex: 1 }} />
        <Link to={`/dmr/call/${booking.id}`} className="notes-btn-link">
          Summary &amp; follow-up
        </Link>
        {canEdit && booking.status === 'scheduled' && (
          <button
            className="notes-btn-primary"
            disabled={complete.isPending}
            onClick={() =>
              window.confirm('Mark this call complete? A summary note is added to their record (and Zoho).') &&
              complete.mutate()
            }
          >
            {complete.isPending ? 'Saving…' : 'Mark call complete'}
          </button>
        )}
      </div>
    </div>
  );
}

function ChecklistItem({
  item,
  value,
  disabled,
  onChange,
}: {
  item: DmrTemplateItem;
  value: DmrResponse | undefined;
  disabled: boolean;
  onChange: (r: DmrResponse | null) => void;
}) {
  if (item.type === 'check') {
    const on = value?.status === 'yes';
    return (
      <label className={`dmr-check-item${on ? ' done' : ''}`}>
        <input type="checkbox" checked={on} disabled={disabled} onChange={() => onChange(on ? null : { status: 'yes' })} />
        <span>{item.label}</span>
      </label>
    );
  }
  if (item.type === 'choice') {
    return (
      <div className="dmr-check-item dmr-check-stacked">
        <span>{item.label}</span>
        <div className="dmr-check-options">
          {(item.options ?? []).map((o) => {
            const on = value?.value === o;
            return (
              <button
                key={o}
                type="button"
                disabled={disabled}
                className={`dmr-check-option${on ? ' on' : ''}`}
                onClick={() => onChange(on ? null : { value: o })}
              >
                {o}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  return (
    <label className="dmr-check-item dmr-check-inline">
      <span>{item.label}</span>
      <input
        type={item.type === 'number' ? 'number' : 'text'}
        disabled={disabled}
        value={value?.value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : { value: e.target.value })}
      />
    </label>
  );
}

const STATUS_LABEL: Record<DmrPastCall['status'], string> = {
  scheduled: 'Not marked complete',
  completed: 'Complete',
  no_show: 'No-show',
  cancelled: 'Cancelled',
};

/** Earlier years' calls, read-only, one row per year (#254). The year is the
 *  year the call took place, so January 2027 calls file under 2027. */
export function DmrPastCalls({ calls }: { calls: DmrPastCall[] }) {
  if (!calls.length) return null;
  return (
    <div className="dmr-past">
      <h4>Past DMR calls</h4>
      {calls.map((c) => {
        const items = c.template?.sections.flatMap((s) => s.items) ?? [];
        const done = items.filter((i) => answered(i, c.responses[i.id])).length;
        return (
          <details key={c.id} className="dmr-past-call">
            <summary>
              <strong>{c.year ?? c.seasonName}</strong>
              <span>
                {fmtWhen(c.startsAt)}
                {c.teamMemberName && <> · with {c.teamMemberName}</>} · {STATUS_LABEL[c.status]}
              </span>
              {items.length > 0 && <span className="dmr-past-count">{done}/{items.length}</span>}
            </summary>
            {c.summary?.trim() && <p className="dmr-past-summary">{c.summary}</p>}
            {c.template?.sections.map((section) => (
              <section key={section.id} className="dmr-check-section">
                <h4>{section.title}</h4>
                {section.items.map((item) => (
                  <ChecklistItem key={item.id} item={item} value={c.responses[item.id]} disabled onChange={() => {}} />
                ))}
              </section>
            ))}
          </details>
        );
      })}
    </div>
  );
}
